import { useEffect, useState, type ReactNode } from 'react';
import { type ISignUpClosedCopy } from '@featit/shared/sign-up-closed';
import {
  OAUTH_PROVIDER_LABELS,
  OAUTH_REFUSALS,
  type OAuthProviderType,
  type OAuthRefusalType,
} from '@featit/shared/types/index';
import type { oauthRefusals } from '@featit/shared/i18n/messages/en/oauthRefusals';
import type { TFn } from '@/i18n/LocaleProvider';

/**
 * What the three provider buttons look like, and what a refusal says.
 *
 * ## Why the refusal sentences live here and not in the backend
 *
 * The backend sends a **code** in a redirect URL — never a message. A message crossing a URL is a
 * message some page has to render, and a URL parameter rendered as text is an injection surface. So
 * the sentence is chosen here, by the code, from the catalogue (`oauthRefusals`, F-0621), a bundle
 * nobody can influence.
 *
 * ⛔ The map (`REFUSAL_WORDS`) is `Record<OAuthRefusalType, …>`, which means adding a refusal to `@featit/shared`
 * without writing its sentence **fails the type check**. The alternative — a lookup with a
 * fallback — reads as careful and quietly renders a bare slug like `identity-claimed` on the one
 * screen where somebody has no other way to find out what happened.
 */

interface IRefusalMessage {
  /** What happened, in the second person, without provider jargon. */
  title: string;
  /** What to do about it. Empty when there is genuinely nothing for the person to do. */
  action: string;
  /** T-0615: a page that is the next step, when there is one (the early-access form). */
  link?: { href: string; label: string };
}

/** Each refusal's words by its key (`oauthRefusals`): a code without words fails the type check. */
const REFUSAL_WORDS: Record<OAuthRefusalType, Exclude<keyof typeof oauthRefusals, 'closedNext'>> = {
  [OAUTH_REFUSALS.EXPIRED]: 'expired',
  [OAUTH_REFUSALS.PROVIDER_REFUSED]: 'providerRefused',
  [OAUTH_REFUSALS.NO_EMAIL]: 'noEmail',
  [OAUTH_REFUSALS.EMAIL_UNVERIFIED]: 'emailUnverified',
  /**
   * ⛔ The refusal that closes the account-takeover route, and the one whose wording matters most.
   *
   * It has to be *useful* — the person in front of it is very often the legitimate owner, who
   * signed up with a password months ago and is now trying Google for the first time. Telling them
   * only "no" would read as a bug. Telling them the exact next step turns a refusal into a
   * one-minute detour, which is what makes refusing affordable instead of a thing somebody later
   * argues should be relaxed.
   */
  [OAUTH_REFUSALS.ACCOUNT_EXISTS]: 'accountExists',
  [OAUTH_REFUSALS.IDENTITY_CLAIMED]: 'identityClaimed',
  [OAUTH_REFUSALS.ALREADY_CONNECTED]: 'alreadyConnected',
  [OAUTH_REFUSALS.DEACTIVATED]: 'deactivated',
  [OAUTH_REFUSALS.NOT_CONFIGURED]: 'notConfigured',
  /**
   * Said to somebody who has no account here and is not going to get one by trying again, so the
   * action names the only thing that actually works. It deliberately does not say *which* rule
   * refused — see the comment on `REGISTRATION_CLOSED` in `@featit/shared`.
   */
  [OAUTH_REFUSALS.REGISTRATION_CLOSED]: 'registrationClosed',
  /** F-0160 AC-53 (I-0638): nobody can create an account until the operator has run setup. */
  [OAUTH_REFUSALS.SETUP_REQUIRED]: 'setupRequired',
};

function refusalIn(
  t: TFn,
  words: Exclude<keyof typeof oauthRefusals, 'closedNext'>,
): IRefusalMessage {
  return {
    title: t(`oauthRefusals.${words}.title`),
    action: t(`oauthRefusals.${words}.action`),
  };
}

/** The codes that have words, for the check that every refusal the backend sends has some. */
export const OAUTH_REFUSAL_CODES = Object.keys(REFUSAL_WORDS) as OAuthRefusalType[];

let refusalWordsIn = false;

/**
 * Puts the refusals' English in the catalogue (`oauthRefusalWords.ts`). ⛔ Never imported statically: the sign-in page is in
 * the entry, and the words are wanted only on the rare load that carries `?oauthError=` (F-0621,
 * the entry budget). German has them with the rest of its catalogue.
 */
export async function loadOAuthRefusals(): Promise<void> {
  if (refusalWordsIn) return;
  await import('./oauthRefusalWords');
  refusalWordsIn = true;
}

/** Whether a refusal in the address can be said yet: its words load first, then it is drawn. */
export function useOAuthRefusalWords(code: string | null | undefined): boolean {
  const [ready, setReady] = useState(refusalWordsIn);
  useEffect(() => {
    if (!code || ready) return;
    let live = true;
    void loadOAuthRefusals().then(() => {
      if (live) setReady(true);
    });
    return () => {
      live = false;
    };
  }, [code, ready]);
  return ready;
}

/**
 * The sentence for whatever came back in `?oauthError=`.
 *
 * ⚠️ The parameter arrives from a URL, so it may be anything at all — including something a person
 * typed. An unrecognised value gets a generic sentence rather than being echoed back onto the
 * screen, which would render an attacker's string inside FEATit's own error box.
 *
 * `closed` is the deployment's closed-sign-up copy when `/.well-known/featit` says sign-up is
 * closed (T-0615, AC-W19c). `registration-closed` names no rule, but on a closed deployment the rule
 * is known, and the public cloud before it opens has no administrator to ask: the visitor is sent
 * to early access, or on a server told to ask whoever runs it.
 */
export function oauthRefusalMessageIn(
  t: TFn,
  code: string | null | undefined,
  closed: ISignUpClosedCopy | null = null,
): IRefusalMessage | null {
  if (!code) return null;
  if (code === OAUTH_REFUSALS.REGISTRATION_CLOSED && closed) {
    return {
      title: closed.lead,
      action: t('oauthRefusals.closedNext', { next: closed.next }),
      ...(closed.earlyAccessUrl
        ? { link: { href: closed.earlyAccessUrl, label: closed.actionLabel } }
        : {}),
    };
  }
  const words = REFUSAL_WORDS[code as OAuthRefusalType] as
    | Exclude<keyof typeof oauthRefusals, 'closedNext'>
    | undefined;
  return refusalIn(t, words ?? 'unknown');
}

export interface IProviderButton {
  provider: OAuthProviderType;
  label: string;
  icon: ReactNode;
}

/**
 * The marks, drawn rather than fetched.
 *
 * Inline SVG so a sign-in screen renders identically with no network, no third-party request from
 * the sign-in page, and nothing for a content-security policy to have an opinion about. Each is
 * `aria-hidden` because the button already carries the provider's name as text — a screen reader
 * announcing "Google Google" is the usual result of decorating an already-labelled control.
 */
export const PROVIDER_BUTTONS: readonly IProviderButton[] = [
  {
    provider: 'google',
    label: OAUTH_PROVIDER_LABELS.google,
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" focusable="false">
        <path
          fill="#4285F4"
          d="M23.06 12.25c0-.85-.08-1.67-.22-2.45H12v4.64h6.2a5.3 5.3 0 0 1-2.3 3.48v2.9h3.72c2.17-2 3.44-4.95 3.44-8.57Z"
        />
        <path
          fill="#34A853"
          d="M12 23.5c3.1 0 5.71-1.03 7.62-2.78l-3.72-2.9c-1.03.7-2.35 1.1-3.9 1.1-3 0-5.55-2.02-6.46-4.75H1.7v2.99A11.5 11.5 0 0 0 12 23.5Z"
        />
        <path
          fill="#FBBC05"
          d="M5.54 14.17a6.9 6.9 0 0 1 0-4.34v-3H1.7a11.5 11.5 0 0 0 0 10.33l3.84-2.99Z"
        />
        <path
          fill="#EA4335"
          d="M12 4.77c1.69 0 3.2.58 4.4 1.72l3.3-3.3C17.7 1.32 15.1.5 12 .5 7.52.5 3.65 3.07 1.7 6.83l3.84 3a6.9 6.9 0 0 1 6.46-5.06Z"
        />
      </svg>
    ),
  },
  {
    provider: 'microsoft',
    label: OAUTH_PROVIDER_LABELS.microsoft,
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" focusable="false">
        <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
        <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
        <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
        <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
      </svg>
    ),
  },
  {
    provider: 'apple',
    label: OAUTH_PROVIDER_LABELS.apple,
    icon: (
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 fill-current text-gray-900 dark:text-gray-100"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M16.36 12.72c.02 2.6 2.28 3.46 2.3 3.47-.02.06-.36 1.24-1.19 2.45-.72 1.05-1.47 2.1-2.65 2.12-1.16.02-1.53-.69-2.86-.69-1.32 0-1.74.67-2.83.71-1.14.04-2-1.13-2.73-2.18-1.49-2.14-2.62-6.06-1.1-8.7A4.25 4.25 0 0 1 8.9 7.72c1.11-.02 2.17.75 2.85.75.68 0 1.96-.93 3.3-.79.56.02 2.14.23 3.15 1.7-.08.05-1.88 1.1-1.86 3.28M14.2 5.9c.6-.73 1-1.74.9-2.75-.86.04-1.9.58-2.52 1.3-.55.65-1.03 1.68-.9 2.67.96.07 1.93-.49 2.52-1.22" />
      </svg>
    ),
  },
];

/**
 * Which providers an account screen lists.
 *
 * ⚠️ The union of what the deployment offers and what this person has already connected — not just
 * the first. A provider that was configured yesterday and is not today still signs somebody in:
 * the link row is in the database and `resolveSignIn` honours it. Dropping it from the list would
 * leave that person holding a credential they can neither see nor disconnect.
 */
export function providersToShow(store: {
  oauthProviders: readonly OAuthProviderType[];
  oauthIdentities: readonly { provider: OAuthProviderType }[];
}): OAuthProviderType[] {
  return [
    ...new Set([
      ...store.oauthProviders,
      ...store.oauthIdentities.map((identity) => identity.provider),
    ]),
  ];
}

/** The buttons for the providers this deployment actually offers, in a stable order. */
export function providerButtonsFor(
  available: readonly OAuthProviderType[],
  { forApp = false }: { forApp?: boolean } = {},
): readonly IProviderButton[] {
  /*
    T-0317, App Review Guideline 4.8: an app that offers a third-party sign-in must offer Sign in
    with Apple beside it. A sign-in for the iPhone app (headed for `/mobile-auth`) on a deployment
    that offers Google or Microsoft but not Apple therefore offers none of them: email, password
    and passkeys remain. The website and the desktop app keep every provider configured.
  */
  const offered = forApp && !available.includes('apple') ? [] : available;
  return PROVIDER_BUTTONS.filter((button) => offered.includes(button.provider));
}

/** Whether a sign-in page's `redirect` hands the session to the iPhone app (`/mobile-auth`). */
export function signInIsForApp(redirect: string | null): boolean {
  return Boolean(redirect && /^\/mobile-auth(\?|$)/.test(redirect));
}
